import React from 'react';
import PropTypes from 'prop-types';

import { Field } from 'react-final-form';
import { Trans } from '@lingui/macro';
import { useStore } from 'effector-react';
import dayjs from 'dayjs';

import { Radio, Label, Checkbox } from '@oca/ui';

import {
  TextFieldWrapper,
  SelectWrapper,
  DayPickerInputWrapper,
} from '@lib/final-form-oca-ui';

import { $departmentList, $employeeList } from '@features/common';

import { AvatarUploader } from '@features/common/organisms/avatar-uploader';
import { OnChange } from '@lib/react-final-form-on-change';
import { notify } from '@lib/notifier';
import { DebouncingValidatingField } from '@lib/debouncing-validating-field';
import { dateTokens } from '@lib/i18n';
import styled from 'styled-components';
import { MARITAL_STATUSES, GENDERS, SHIRT_SIZES } from '../model/static';
import { validateUsername } from '../model/validation';

/**
 *
 * @param {{ initialValues: import('../model/profile').Employee }}
 */

const normalize = v => (v === null || v === undefined ? '' : String(v).trim());
const sameId = (a, b) =>
  normalize(a) !== '' && normalize(b) !== '' && normalize(a) === normalize(b);

const validateRfidUnique = (employeeList, initialValues) => (
  val,
  allValues,
  meta,
) => {
  const normalizedVal = normalize(val);
  if (!normalizedVal) return undefined;

  const initialFieldVal = normalize(meta && meta.initial);
  if (initialFieldVal && initialFieldVal === normalizedVal) return undefined;

  const currentId = initialValues && initialValues.id;

  const list = Array.isArray(employeeList) ? employeeList : [];

  const isDuplicate = list.some(emp => {
    if (!emp) return false;

    const empRfid = normalize(emp.rfid);
    if (!empRfid) return false;

    if (sameId(emp.id, currentId)) return false;

    return empRfid === normalizedVal;
  });

  return isDuplicate ? 'This RFID is already assigned to someone' : undefined;
};

export function FormBasic({
  initialValues,
  maritalStatuses,
  genders,
  shirtSizes,
}) {
  const departments = useStore($departmentList);
  const employeeList = useStore($employeeList);

  const rfidValidator = validateRfidUnique(employeeList, initialValues);

  return (
    <div>
      {/* Compact Top Bar: Profile Photo & Residency */}
      <CompactTopBar>
        <Field
          name="avatar"
          format={val => (val ? val.fileUrl || val : '')}
          render={({ input }) => (
            <AvatarUploader
              size={48}
              src={input.value}
              onUploaded={input.onChange}
            />
          )}
        />
        <TopBarDivider />
        <ResidencyRow>
          <Label style={{ fontWeight: 600, margin: 0 }}>
            <Trans>Residency:</Trans>
          </Label>
          <Field
            name="isResident"
            render={({ input }) => (
              <Radio
                checked={input.value}
                value="resident"
                onChange={event => input.onChange(event.target.checked)}
                label={<Trans>Resident</Trans>}
              />
            )}
          />
          <Field
            name="isResident"
            render={({ input }) => (
              <Radio
                checked={input.value === false}
                value="non_resident"
                onChange={event => input.onChange(!event.target.checked)}
                label={<Trans>Non Resident</Trans>}
              />
            )}
          />
        </ResidencyRow>
        <OnChange name="isResident">
          {(value, prevValue) => {
            if (value === false && prevValue === true) {
              notify.warn(
                <Trans>
                  Please fill in employee{"'"}s Visa and Work permit information
                  in documents tab
                </Trans>,
                { autoClose: 10000 },
              );
            }
          }}
        </OnChange>
      </CompactTopBar>

      {/* Row 1: Name fields (3 inputs) */}
      <FormGrid>
        <FormCol>
          <Field
            name="firstName"
            component={TextFieldWrapper}
            label={<Trans>First name</Trans>}
            required
          />
        </FormCol>
        <FormCol>
          <Field
            name="middleName"
            label={<Trans>Middle name</Trans>}
            component={TextFieldWrapper}
          />
        </FormCol>
        <FormCol>
          <Field
            name="lastName"
            component={TextFieldWrapper}
            label={<Trans>Last name</Trans>}
            required
          />
        </FormCol>
      </FormGrid>

      {/* Row 2: Personal attributes (3 inputs) */}
      <FormGrid>
        <FormCol>
          <Field
            name="birthday"
            label={<Trans>Birthday</Trans>}
            parse={val => (val ? dayjs(val).format(dateTokens.ISO_DATE) : '')}
            format={val => (val ? new Date(val).toISOString() : null)}
            component={DayPickerInputWrapper}
            options={{
              formatDate: date => dayjs(date).format('L'),
            }}
            required
          />
        </FormCol>
        <FormCol>
          <Field
            name="gender"
            component={SelectWrapper}
            label={<Trans>Gender</Trans>}
            parse={val => (val ? val.value : '')}
            format={val =>
              val ? genders.find(({ value }) => val === value) : ''
            }
            options={genders}
          />
        </FormCol>
        <FormCol>
          <Field
            name="maritalStatus"
            component={SelectWrapper}
            parse={val => (val ? val.value : '')}
            format={val =>
              val ? maritalStatuses.find(({ value }) => val === value) : ''
            }
            label={<Trans>Marital status</Trans>}
            options={maritalStatuses}
          />
        </FormCol>
      </FormGrid>

      {/* Row 3: Account & Identity (3 inputs) */}
      <FormGrid>
        <FormCol>
          <DebouncingValidatingField
            name="username"
            timeout={700}
            component={TextFieldWrapper}
            label={<Trans>Username</Trans>}
            validate={val => usernameAvailable(val, initialValues)}
            required
          />
        </FormCol>
        <FormCol>
          <Field
            name="rfid"
            component={TextFieldWrapper}
            label={<Trans>RFID</Trans>}
            validate={rfidValidator}
          />
        </FormCol>
        <FormCol>
          <Field
            name="nationality"
            component={TextFieldWrapper}
            label={<Trans>Nationality</Trans>}
          />
        </FormCol>
      </FormGrid>

      {/* Row 4: Job & Organization (3 inputs) */}
      <FormGrid>
        <FormCol>
          <Field
            name="department"
            component={SelectWrapper}
            label={<Trans>Department</Trans>}
            options={departments}
            parse={val => (val ? val.id : '')}
            format={val =>
              val ? departments.find(({ id }) => (val.id || val) === id) : ''
            }
            required
          />
        </FormCol>
        <FormCol>
          <Field
            name="jobTitle"
            component={TextFieldWrapper}
            label={<Trans>Job title</Trans>}
            required
          />
        </FormCol>
        <FormCol>
          <Field
            name="shirtSize"
            component={SelectWrapper}
            parse={val => (val ? val.value : '')}
            format={val =>
              val ? shirtSizes.find(({ value }) => val === value) : ''
            }
            label={<Trans>Shirt size</Trans>}
            options={shirtSizes}
          />
        </FormCol>
      </FormGrid>

      {/* Row 5: Employment & Access (3 inputs/columns) */}
      <FormGrid>
        <FormCol>
          <Field
            name="hiredDate"
            label={<Trans>Hired date</Trans>}
            parse={val => (val ? dayjs(val).format(dateTokens.ISO_DATE) : '')}
            format={val => (val ? new Date(val).toISOString() : null)}
            component={DayPickerInputWrapper}
            options={{ formatDate: date => dayjs(date).format('LL') }}
          />
        </FormCol>
        <FormCol>
          <Label style={{ marginBottom: 6, display: 'block', fontWeight: 600 }}>
            <Trans>Employment status</Trans>
          </Label>
          <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
            <Field
              type="radio"
              name="employmentStatus"
              value="full_time"
              render={({ input }) => (
                <Radio {...input} label={<Trans>Full time</Trans>} />
              )}
            />
            <Field
              type="radio"
              name="employmentStatus"
              value="part_time"
              render={({ input }) => (
                <Radio {...input} label={<Trans>Part time</Trans>} />
              )}
            />
          </div>
        </FormCol>
        <FormCol>
          <Label style={{ marginBottom: 6, display: 'block', fontWeight: 600 }}>
            <Trans>Permissions &amp; Role</Trans>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Field
              name="position"
              render={({ input }) => {
                const isManager = input.value === 'manager';
                const isDirector = input.value === 'director';

                return (
                  <div
                    style={{ visibility: isDirector ? 'hidden' : 'visible' }}
                  >
                    <Checkbox
                      onChange={() =>
                        input.onChange(isManager ? 'expert' : 'manager')
                      }
                      value="manager"
                      checked={isManager}
                      disabled={isDirector}
                      label={<Trans>Department manager</Trans>}
                    />
                  </div>
                );
              }}
            />
            <Field
              type="checkbox"
              name="isMatrixActive"
              render={({ input }) => (
                <Checkbox
                  {...input}
                  label={<Trans>Access to messenger</Trans>}
                />
              )}
            />
          </div>
        </FormCol>
      </FormGrid>
    </div>
  );
}

const CompactTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TopBarDivider = styled.div`
  width: 1px;
  height: 32px;
  background: #cbd5e1;
`;

const ResidencyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCol = styled.div`
  width: 100%;
  min-width: 0;
`;

FormBasic.propTypes = {
  initialValues: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  maritalStatuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    }),
  ),
  genders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    }),
  ),
  shirtSizes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    }),
  ),
};

FormBasic.defaultProps = {
  maritalStatuses: [
    { value: MARITAL_STATUSES.MARRIED, label: <Trans>Married</Trans> },
    { value: MARITAL_STATUSES.SINGLE, label: <Trans>Single</Trans> },
  ],
  genders: [
    { value: GENDERS.MALE, label: <Trans>Male</Trans> },
    { value: GENDERS.FEMALE, label: <Trans>Female</Trans> },
  ],
  shirtSizes: Object.values(SHIRT_SIZES).map(value => ({
    value,
    label: value,
  })),
};

async function usernameAvailable(value, initialValues) {
  if (!value) {
    return <Trans>This field is required</Trans>;
  }

  if (initialValues && initialValues.username !== value) {
    const error = await validateUsername(value);

    return error || undefined;
  }
  return undefined;
}
